import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '../icons'

/**
 * An image at its real size, as big as the screen allows.
 *
 * The thumbnail in the thread is capped at 220px so a photo does not shove the conversation
 * around, which is right for reading and useless for looking: customers send photos of a wall to
 * be examined — a texture, a socket, the state of a peeled decal — and 220px answers none of
 * that.
 *
 * `max-width/height: 100%` with `object-fit: contain` shows the image at its own resolution when
 * it fits and shrinks it only when it does not, so a small photo is never blown up into mush.
 */
export function Lightbox({ url, caption, onClose }: { url: string; caption?: string | null; onClose: () => void }) {
  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onEsc)
    // The thread behind it scrolls otherwise, and the image appears to drift while it is open.
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onEsc)
      document.body.style.overflow = previous
    }
  }, [onClose])

  return createPortal(
    <div className="lightbox" onClick={onClose}>
      <button className="lightbox-x" onClick={onClose} title="Fechar (Esc)" aria-label="Fechar">
        <Icon name="x" size={18} />
      </button>
      {/* Clicking the image itself must not close it — only the surrounding backdrop does. */}
      <img src={url} alt={caption ?? ''} onClick={(e) => e.stopPropagation()} />
      {caption && <div className="lightbox-cap" onClick={(e) => e.stopPropagation()}>{caption}</div>}
    </div>,
    document.body,
  )
}
